import { useEffect } from 'react';
import { Search, RefreshCw } from 'lucide-react';
import { useMemoryStore } from '../../stores/memoryStore';
import FileTree from './FileTree';
import MarkdownViewer from './MarkdownViewer';
import { Spinner } from '../common/Spinner';

function HealthBar() {
  const { health } = useMemoryStore();
  if (!health) return null;

  const percent = (health.memoryMdSize / health.memoryMdLimit) * 100;
  const sizeKb = (health.memoryMdSize / 1024).toFixed(1);
  const limitKb = (health.memoryMdLimit / 1024).toFixed(0);

  let barColor = 'bg-success';
  if (percent > 95) barColor = 'bg-error';
  else if (percent > 75) barColor = 'bg-warning';

  return (
    <div className="flex items-center gap-2">
      <div className="w-32 h-2 bg-bg-tertiary rounded-full overflow-hidden">
        <div
          className={`h-full ${barColor} rounded-full transition-all duration-300`}
          style={{ width: `${Math.min(percent, 100)}%` }}
        />
      </div>
      <span className="text-xs text-text-muted whitespace-nowrap">
        {sizeKb} KB / {limitKb} KB
      </span>
    </div>
  );
}

export default function MemoryPage() {
  const {
    sections,
    loading,
    searchQuery,
    selectedPath,
    fetchFiles,
    fetchHealth,
    setSearchQuery,
    setSelectedPath,
  } = useMemoryStore();

  // Initial load
  useEffect(() => {
    fetchFiles();
    fetchHealth();
  }, [fetchFiles, fetchHealth]);

  // Auto-select MEMORY.md on initial load
  useEffect(() => {
    if (sections.length > 0 && !selectedPath) {
      const memorySection = sections.find(s => s.label === 'Long-Term Memory');
      const memoryFile = memorySection?.files.find(f => f.name === 'MEMORY.md');
      if (memoryFile) {
        setSelectedPath(memoryFile.path);
      }
    }
  }, [sections, selectedPath, setSelectedPath]);

  const handleRefresh = () => {
    fetchFiles();
    fetchHealth();
  };

  return (
    <div className="flex flex-col h-[calc(100vh-3.5rem)]">
      {/* Top bar */}
      <div className="flex items-center gap-3 px-4 py-2.5 border-b border-border-subtle bg-bg-secondary shrink-0">
        {/* Search */}
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-text-muted" />
          <input
            type="text"
            placeholder="Search files..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full bg-bg-tertiary border border-border-subtle rounded-md pl-8 pr-3 py-1.5 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent-purple transition-colors"
          />
        </div>

        {/* Health bar */}
        <HealthBar />

        {/* Refresh */}
        <button
          onClick={handleRefresh}
          className="p-1.5 rounded-md text-text-muted hover:text-text-primary hover:bg-bg-tertiary transition-colors"
          title="Refresh"
        >
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>

      {/* Split panel */}
      <div className="flex flex-col md:flex-row flex-1 min-h-0">
        {/* Left: File tree */}
        <div className="w-full md:w-72 border-b md:border-b-0 md:border-r border-border-subtle overflow-y-auto bg-bg-secondary shrink-0 max-h-48 md:max-h-none">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner />
            </div>
          ) : (
            <FileTree />
          )}
        </div>

        {/* Right: Markdown viewer */}
        <div className="flex-1 min-w-0 overflow-hidden">
          <MarkdownViewer />
        </div>
      </div>
    </div>
  );
}
